import React, { cloneElement, useEffect, useState } from "react";
import {
  Button,
  Datagrid,
  Filter,
  FunctionField,
  ReferenceField,
  ResourceContextProvider,
  sanitizeListRestProps,
  SearchInput,
  ShowProps,
  TextField,
  TopToolbar,
  useDataProvider,
  useListContext,
} from "react-admin";
import { Grid, Typography } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import CustomDateField from "components/CustomDateField";
import IconPlus from "@material-ui/icons/Add";
import DeleteNotTakingReportButton from "./components/DeleteNotTakingReportButton";
import BusDataField from "./components/BusDataField";
import UpdatedByField from "../../components/UpdatedByField";
import { PaddingDenseStyled2List } from "../../components/ListWrapper";
import CustomLabeled from "../../components/layout/CustomLabeled";
import CustomPagination from "../../components/CustomPagination";

interface INotTakingReportTable extends ShowProps {
  isCanceled?: boolean;
}

const ThisFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
  </Filter>
);

const ListActions = (props: any) => {
  const { className, exporter, filters, maxResults, history, ...rest } = props;
  const { resource, displayedFilters, filterValues, showFilter } =
    useListContext();
  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: "button",
        })}

      <Button
        onClick={() =>
          history.push(
            `/notTakingReports/create?source=${JSON.stringify({
              studentId: Number(props.filterValues?.StudentId),
            })}&studentId=${Number(props.filterValues?.StudentId)}`
          )
        }
        label="Create Not Taking Report"
      >
        <IconPlus />
      </Button>
    </TopToolbar>
  );
};

const NotTakingReportTable = ({ isCanceled, ...props }: INotTakingReportTable) => {
  return (
    <ResourceContextProvider value="notTakingReports">
      <PaddingDenseStyled2List
        {...props}
        exporter={false}
        hasCreate={false}
        filters={<ThisFilter />}
        filterDefaultValues={{
          StudentId: Number(props.id),
          IsCanceled: isCanceled
        }}
        sort={{ field: "date", order: "DESC" }}
        bulkActionButtons={false}
        title=" "
        actions={isCanceled ? false : <ListActions history={props.history} />}
        pagination={<CustomPagination />}
      >
        <Datagrid>
          <TextField source="id" emptyText="-" />
          <ReferenceField
            source="busSchedulePlanId"
            label="Wave Name"
            reference="busSchedulePlans"
            link={false}
          >
            <ReferenceField
              source="busScheduleId"
              reference="busSchedules"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </ReferenceField>
          <CustomDateField source="date" />

          {/* Conditionally show Updated By, Updated At and Delete Button column */}
          {/* Use array to separate to each column */}
          {!isCanceled && [
            <FunctionField
              label="Updated By"
              render={(record: any) => (
                <>
                  {record.updatedBy === null ? (
                    "-"
                  ) : (
                    <UpdatedByField data={record.updatedBy} />
                  )}
                </>
              )}
            />,
            <CustomDateField source="updatedAt" isDateTime />,
            <FunctionField
              label="Action"
              render={(record: any) => (
                <DeleteNotTakingReportButton
                  notTakingReportId={record.id}
                />
              )}
            />
          ]}

          {/* Conditionally show Canceled By and Canceled At column */}
          {/* Use array to separate to each column */}
          {isCanceled && [
            <FunctionField
              label="Canceled By"
              render={(record: any) => (
                <>
                  {record.deletedBy === null ? (
                    "-"
                  ) : (
                    <UpdatedByField
                      data={record.deletedBy}
                      referenceSource="deletedBy.id"
                    />
                  )}
                </>
              )}
            />,
            <CustomDateField label="Canceled At" source="deletedAt" isDateTime />
          ]}
        </Datagrid>
      </PaddingDenseStyled2List>
    </ResourceContextProvider>
  )
}

const Route = (props: ShowProps) => {
  const [routeData, setRouteData] = useState<IRouteData[]>([]);
  const provider = useDataProvider();

  useEffect(() => {
    provider
      .customGetOne(`students/${props.id}/routeNames`)
      .then(({ data }: any) => {
        setRouteData(data as IRouteData[]);
      });
  }, []);

  return (
    <>
      <SanitizedGrid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">Route Detail</Typography>
        </Grid>

        <Grid item xs={4}>
          <CustomLabeled name="Am Bus">
            <BusDataField type="to_school" data={routeData} label="AM Bus" />
          </CustomLabeled>
        </Grid>
        <Grid item xs={8}></Grid>

        <Grid item xs={4}>
          <CustomLabeled name="Pm Bus">
            <BusDataField type="from_school" data={routeData} label="PM Bus" />
          </CustomLabeled>
        </Grid>
        <Grid item xs={8}></Grid>

        <Grid item xs={4}>
          <CustomLabeled name="Cca Bus">
            <BusDataField type="cca" data={routeData} label="CCA Bus" />
          </CustomLabeled>
        </Grid>
        <Grid item xs={8}></Grid>

        <Grid item xs={12}>
          <Typography variant="h6">Not Taking Reports</Typography>
        </Grid>

        <Grid item xs={12}>
          <NotTakingReportTable {...props} />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6">Canceled Not Taking Logs</Typography>
        </Grid>

        <Grid item xs={12}>
          <NotTakingReportTable {...props} isCanceled />
        </Grid>
      </SanitizedGrid>
    </>
  );
};

export default Route;
